import { Copy, MessageSquare } from "@tamagui/lucide-icons";
import { useContext, useState } from "react";
import { Path, Svg } from "react-native-svg";

import { Feedback } from "@/api/generated/models";
import { useUpdateEvent } from "@/api/mutations/conversation";
import { ChatIcon, XStack } from "@/components";
import { LayoutContext } from "@/components/LayoutProvider";
import { isDev, isLocal } from "@/config/env";

type ThumbProps = {
  size: number;
  color: string;
};

const SVG_SIZE_PROPS = {
  width: 24,
  height: 24,
  viewBox: "6 0 18 24",
};

const ThumbDownSolid = ({ color }: ThumbProps) => (
  <Svg {...SVG_SIZE_PROPS} fill="none">
    <Path
      fill={color}
      d="M21.381 15.943a1.619 1.619 0 0 0 .017-3.237 1.619 1.619 0 0 0-.017-3.238h-.34a1.623 1.623 0 0 0-.16-3.238h-.84A1.619 1.619 0 0 0 19.886 3H12.61c-.058 0-.116.003-.173.01H9.001a1 1 0 0 0-1 1L8 12.4c0 .02 0 .04.002.059v3.418c0 .203.06.4.173.566l4.176 6.146a.936.936 0 0 0 1.115.347 2.88 2.88 0 0 0 1.68-3.58l-.727-2.208a.918.918 0 0 1 .872-1.205h6.09ZM6.502 15.888H2a1 1 0 0 1-1-1V4.19a1 1 0 0 1 1-1h4.502L6.5 12c0 .029 0 .058.002.086v3.802Z"
    />
  </Svg>
);

const ThumbDownOutline = ({ color }: ThumbProps) => (
  <Svg {...SVG_SIZE_PROPS} fill="none">
    <Path
      stroke={color}
      strokeLinecap="round"
      strokeWidth={2}
      d="M21.398 12.706a1.619 1.619 0 0 1-.017 3.237h-6.09a.918.918 0 0 0-.872 1.205l.727 2.208a2.88 2.88 0 0 1-1.68 3.58.936.936 0 0 1-1.115-.347l-4.176-6.146a1.007 1.007 0 0 1-.173-.566V12.46a1.015 1.015 0 0 1-.002-.06l.002-8.39a1 1 0 0 1 1-1h3.436c.057-.006.115-.009.173-.009h7.274a1.619 1.619 0 0 1 .156 3.23h.84a1.623 1.623 0 0 1 .16 3.238m.357 3.238a1.619 1.619 0 0 0-.017-3.238h-.34m.357 3.238h-1.896m1.538-3.238h-1.538m1.24-3.238h-1.24M2 3.19h5a1 1 0 0 1 1 1v11.598a.1.1 0 0 1-.1.1H2a1 1 0 0 1-1-1V4.19a1 1 0 0 1 1-1Z"
    />
  </Svg>
);

const ThumbUpSolid = ({ color }: ThumbProps) => (
  <Svg {...SVG_SIZE_PROPS} fill="none">
    <Path
      fill={color}
      d="M21.381 8.057a1.619 1.619 0 0 1 .017 3.237 1.619 1.619 0 0 1-.017 3.238h-.34a1.623 1.623 0 0 1-.16 3.238h-.84a1.62 1.62 0 0 1-.156 3.23H12.61c-.058 0-.116-.003-.173-.01H9.001a1 1 0 0 1-1-1L8 11.6c0-.02 0-.04.002-.059V8.123c0-.203.06-.4.173-.566l4.176-6.146a.935.935 0 0 1 1.115-.347 2.88 2.88 0 0 1 1.68 3.58l-.727 2.208a.918.918 0 0 0 .872 1.205h6.09ZM6.502 8.112H2a1 1 0 0 0-1 1v10.697a1 1 0 0 0 1 1h4.502L6.5 12c0-.029 0-.058.002-.086V8.112Z"
    />
  </Svg>
);

const ThumbUpOutline = ({ color }: ThumbProps) => (
  <Svg {...SVG_SIZE_PROPS} fill="none">
    <Path
      stroke={color}
      strokeLinecap="round"
      strokeWidth={2}
      d="M21.398 11.294a1.619 1.619 0 0 0-.017-3.237h-6.09a.918.918 0 0 1-.872-1.205l.727-2.208a2.88 2.88 0 0 0-1.68-3.58.935.935 0 0 0-1.115.347L8.175 7.557a1.007 1.007 0 0 0-.173.566v3.418l-.002.06.002 8.39a1 1 0 0 0 1 1h3.436c.057.006.115.009.173.009h7.274a1.619 1.619 0 0 0 .156-3.23h.84a1.623 1.623 0 0 0 .16-3.238h.34m.017-3.238a1.619 1.619 0 0 1-.017 3.238m.017-3.238h-1.896m1.88 3.238h-1.88m1.24 3.238h-1.24M2 20.81h5a1 1 0 0 0 1-1V8.211a.1.1 0 0 0-.1-.1H2a1 1 0 0 0-1 1v10.697a1 1 0 0 0 1 1Z"
    />
  </Svg>
);

const Thumb = ({
  isSatisfied,
  up,
  handSelection,
}: {
  isSatisfied: Feedback;
  up: boolean;
  handSelection: (isSatisfied: Feedback) => void;
}) => {
  const active = isSatisfied === (up ? 1 : -1);
  let Icon = up ? ThumbUpOutline : ThumbDownOutline;
  if (active) {
    Icon = up ? ThumbUpSolid : ThumbDownSolid;
  }

  return (
    <ChatIcon
      active={active}
      ariaLabel={up ? "Good response" : "Poor response"}
      onPress={() => handSelection(up ? 1 : -1)}
      Icon={Icon}
    />
  );
};

const MessageSatisfaction = ({
  messageId,
  feedback,
  feedbackComment,
  onCopy,
}: {
  messageId: string;
  feedback: Feedback;
  feedbackComment?: string;
  onCopy: () => void;
}) => {
  const { setFeedbackModalOpen, setFeedbackModalData } =
    useContext(LayoutContext);
  const { mutate: updateEvent } = useUpdateEvent();
  const [isSatisfied, setIsSatisfied] = useState<Feedback>(feedback);

  // Only show feedback for local and dev
  if (!isLocal && !isDev) {
    return null;
  }

  const handSelection = (isSatisfied: Feedback) => {
    setIsSatisfied(isSatisfied);
    updateEvent({ eventId: messageId, data: { feedback: isSatisfied } });
  };

  const hasComment = !!feedbackComment;

  return (
    <>
      <XStack alignSelf="flex-start" marginTop="$100">
        <ChatIcon
          active={false}
          Icon={Copy}
          ariaLabel="Copy"
          onPress={onCopy}
        />
        <Thumb isSatisfied={isSatisfied} up handSelection={handSelection} />
        <Thumb
          isSatisfied={isSatisfied}
          up={false}
          handSelection={handSelection}
        />
        <ChatIcon
          active={hasComment}
          Icon={MessageSquare}
          ariaLabel={hasComment ? "Edit feedback" : "Leave feedback"}
          onPress={() => {
            setFeedbackModalOpen(true);
            setFeedbackModalData({
              messageId,
              initialComment: feedbackComment,
            });
          }}
          disableWhenActive={false}
        />
      </XStack>
    </>
  );
};

export default MessageSatisfaction;
